import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import DestinationCard from './DestinationCard';
import { destinationRegions } from '../data/destinations';
import { Globe } from 'lucide-react';

const DestinationsSection = () => {
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState('all');

  const handleDestinationSelect = (destination) => {
    navigate(`/destination/${destination.id}`);
  };

  const getVisibleDestinationsCount = () => {
    if (selectedRegion === 'all') {
      return Object.values(destinationRegions).reduce((total, region) => total + region.destinations.length, 0);
    }
    return destinationRegions[selectedRegion]?.destinations.length || 0;
  };

  return (
    <section className="py-24 px-6 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-20">
          <Badge 
            variant="outline" 
            className="mb-8 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <Globe className="w-4 h-4 mr-2" />
            Destinations Exclusive
          </Badge>
          
          <h2 
            className="text-4xl md:text-6xl font-light mb-8 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Nos Destinations d'Exception
          </h2>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Des lieux d'exception soigneusement sélectionnés où le luxe rencontre l'authenticité,
            pour des expériences inoubliables aux quatre coins du monde.
          </p>

          <div className="text-center">
            <Badge variant="outline" className="px-4 py-2 text-base bg-amber-50 border-amber-200 text-amber-800">
              {getVisibleDestinationsCount()} destinations • 200+ villas d'exception
            </Badge>
          </div>
        </div>

        {/* Region Filter Buttons */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          <Button
            variant={selectedRegion === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedRegion('all')}
            className={`px-6 py-3 rounded-full transition-all duration-300 ${
              selectedRegion === 'all' 
                ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white shadow-lg transform -translate-y-1' 
                : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
            }`}
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: '500',
              letterSpacing: '0.025em'
            }}
          >
            Toutes les Régions
          </Button>

          {Object.entries(destinationRegions).map(([key, region]) => (
            <Button
              key={key}
              variant={selectedRegion === key ? 'default' : 'outline'}
              onClick={() => setSelectedRegion(key)}
              className={`px-6 py-3 rounded-full transition-all duration-300 ${
                selectedRegion === key 
                  ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg transform -translate-y-1' 
                  : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
              }`}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: '500',
                letterSpacing: '0.025em'
              }}
            >
              {region.name}
            </Button>
          ))}
        </div>

        {/* Destinations Grid by Region */}
        {selectedRegion === 'all' ? (
          // Show all regions when 'all' is selected
          <div className="space-y-20">
            {Object.entries(destinationRegions).map(([regionKey, region]) => (
              <div key={regionKey} className="space-y-8">
                
                {/* Region Header - Minimalist */}
                <div className="text-center">
                  <h3 
                    className="text-2xl font-light text-gray-800 mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {region.name}
                  </h3>
                  <div className="w-12 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-4" />
                  <p 
                    className="text-gray-600 italic text-sm max-w-md mx-auto"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {region.description}
                  </p>
                </div>

                {/* Region Destinations Grid - Always Visible, Limited to 4 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {region.destinations.slice(0, 4).map((destination) => (
                    <DestinationCard
                      key={destination.id}
                      destination={destination}
                      onSelect={handleDestinationSelect}
                    />
                  ))}
                </div>

                {/* Show More Button if more than 4 destinations */}
                {region.destinations.length > 4 && (
                  <div className="text-center">
                    <Button
                      variant="ghost"
                      onClick={() => setSelectedRegion(regionKey)}
                      className="text-gray-500 hover:text-amber-600 font-light text-sm border border-gray-200 hover:border-amber-200 px-6 py-2 rounded-full transition-all duration-300"
                    >
                      Voir toutes les destinations ({region.destinations.length})
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Show selected region only
          <div className="space-y-8">
            <div className="text-center">
              <h3 
                className="text-3xl font-light text-gray-800 mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {destinationRegions[selectedRegion]?.name}
              </h3>
              <p 
                className="text-gray-600 italic text-lg"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {destinationRegions[selectedRegion]?.description}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {destinationRegions[selectedRegion]?.destinations.map((destination) => (
                <DestinationCard
                  key={destination.id}
                  destination={destination}
                  onSelect={handleDestinationSelect}
                />
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto border border-white/20">
            <h4 
              className="text-2xl font-light mb-4 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Une destination vous inspire ?
            </h4>
            <p 
              className="text-gray-600 mb-6 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Laissez notre équipe de concierges experts créer votre expérience sur-mesure 
              dans la destination de vos rêves.
            </p>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              Planifier mon Voyage
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DestinationsSection;